/**
 * KOAI Settings Page
 * 시스템 설정 페이지
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Language as LanguageIcon,
  Notifications as NotificationIcon,
  Psychology as AiIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  Palette as ThemeIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { ragAPI, healthAPI, usersAPI } from '../services/api';
import { SUPPORTED_LANGUAGES } from '../locales/i18n';
import { getLevelNumber } from '../config/menuConfig';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Settings state
  const [language, setLanguage] = useState(i18n.language);
  const [notifications, setNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [defaultModel, setDefaultModel] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [models, setModels] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  
  // SaaS: 플랫폼 관리자만 모델 선택
  const canSelectModel = !!(user && (user.isAdmin || user.role === 'admin' || getLevelNumber(user) >= 4));

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Load RAG status for models
      const ragResponse = await ragAPI.getStatus();
      const ragData = ragResponse.data.data;
      setModels(ragData.ollama?.models || []);
      setDefaultModel(ragData.defaultModel || '');

      // Load user's selected model (if Level4+)
      if (canSelectModel && user) {
        setSelectedModel(user.selected_model || '');
      }

      // Load health status
      const healthResponse = await healthAPI.check();
      setSystemHealth(healthResponse.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    setLanguage(lang);
    i18n.changeLanguage(lang).then(() => {
      localStorage.setItem('language', lang);
      setSnackbar({
        open: true,
        message: t('settings.saveSuccess'),
        severity: 'success'
      });
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleModelChange = async (e) => {
    const newModel = e.target.value;
    if (!canSelectModel || !user) {
      return;
    }

    try {
      setLoading(true);
      await usersAPI.update(user.id, { selected_model: newModel });
      setSelectedModel(newModel);
      
      // 사용자 정보 다시 로드하여 context 업데이트
      if (refreshUser) {
        await refreshUser();
      }
      
      setSnackbar({
        open: true,
        message: 'AI 모델이 변경되었습니다. 새 채팅 세션부터 적용됩니다.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to update model:', error);
      setSnackbar({
        open: true,
        message: '모델 변경에 실패했습니다.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('settings.title')}
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<LanguageIcon />} label="일반" />
          <Tab icon={<AiIcon />} label="AI 설정" />
          <Tab icon={<StorageIcon />} label="시스템" />
          <Tab icon={<InfoIcon />} label="정보" />
        </Tabs>
      </Paper>

      {/* General Settings Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Profile Link */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6">
                      <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      프로필 관리
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      이름, 비밀번호 등 개인 정보를 관리합니다.
                    </Typography>
                  </Box>
                  <Button variant="outlined" onClick={() => navigate('/profile')}>
                    프로필 설정
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Language */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <LanguageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('settings.language')}
                </Typography>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>{t('settings.language')}</InputLabel>
                  <Select
                    value={language}
                    label={t('settings.language')}
                    onChange={handleLanguageChange}
                  >
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Notifications */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <NotificationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('settings.notifications')}
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="이메일 알림"
                      secondary="중요 알림을 이메일로 받습니다."
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notifications}
                        onChange={(e) => setNotifications(e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Theme */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ThemeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  테마
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="다크 모드"
                      secondary="어두운 테마를 사용합니다."
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={darkMode}
                        onChange={(e) => setDarkMode(e.target.checked)}
                        disabled
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
                <Typography variant="caption" color="text.secondary">
                  (준비 중)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* AI Settings Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    <AiIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    사용 가능한 AI 모델
                  </Typography>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={loadSettings}
                    disabled={loading}
                  >
                    새로고침
                  </Button>
                </Box>
                
                {loading ? (
                  <CircularProgress />
                ) : models.length === 0 ? (
                  <Alert severity="warning">
                    사용 가능한 모델이 없습니다. Ollama가 실행 중인지 확인하세요.
                  </Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {models.map((model, index) => (
                      <Chip
                        key={index}
                        label={model}
                        color={model === defaultModel ? 'primary' : 'default'}
                        variant={model === defaultModel ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>기본 모델 (서버 설정)</Typography>
                {defaultModel ? (
                  <Box sx={{ mb: 1 }}>
                    <Chip 
                      label={defaultModel} 
                      color="primary" 
                      variant="filled"
                      sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    기본 모델이 설정되지 않았습니다.
                  </Typography>
                )}
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <Select
                    value={defaultModel}
                    displayEmpty
                    disabled
                  >
                    {models.map((model, index) => (
                      <MenuItem key={index} value={model}>{model}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  기본 모델은 서버 설정(.env)에서 변경할 수 있습니다.
                </Typography>

                {canSelectModel && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>사용할 AI 모델 (Level4, Level5 전용)</Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={selectedModel || ''}
                        displayEmpty
                        onChange={handleModelChange}
                        disabled={loading || models.length === 0}
                      >
                        <MenuItem value="">
                          <em>기본 모델 사용 ({defaultModel})</em>
                        </MenuItem>
                        {models.map((model, index) => (
                          <MenuItem key={index} value={model}>{model}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary">
                      선택한 모델이 채팅에서 사용됩니다. 비어있으면 기본 모델을 사용합니다.
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* System Tab */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  서비스 상태
                </Typography>
                
                {loading ? (
                  <CircularProgress />
                ) : systemHealth ? (
                  <List>
                    <ListItem>
                      <ListItemText primary="서버" secondary={systemHealth.status} />
                      <Chip 
                        label={systemHealth.status === 'healthy' ? '정상' : '오류'} 
                        color={systemHealth.status === 'healthy' ? 'success' : 'error'} 
                        size="small" 
                      />
                    </ListItem>
                    <Divider />
                    {Object.entries(systemHealth.services || {}).map(([key, value]) => (
                      <ListItem key={key}>
                        <ListItemText 
                          primary={key.charAt(0).toUpperCase() + key.slice(1)} 
                        />
                        <Chip 
                          label={value === 'connected' ? '연결됨' : '연결 안 됨'} 
                          color={value === 'connected' ? 'success' : 'default'} 
                          size="small" 
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Alert severity="error">시스템 상태를 불러올 수 없습니다.</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Info Tab */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  시스템 정보
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="애플리케이션" secondary="KOAI (Knowledge + Organization + AI)" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="버전" secondary={systemHealth?.version || '1.0.0'} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="환경" secondary={systemHealth?.environment || 'development'} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="GitHub" secondary="https://github.com/kznetwork/koai" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
